import { useTheme } from "@mui/material";
import { ResizeComponent, SizeProps } from "containers/hooks/ResizeComponent";
import * as d3 from "d3";
import { HierarchyRectangularNode } from "d3";
import { getProvider } from "fileParsing/definitions";
import { Category, FileCategory, ProviderFile } from "fileParsing/types";
import { AlgoStep } from "fileParsing/wrangler/types";
import React, { useMemo, useState } from "react";
import Tooltip from "../../Components/Tooltip";

export interface SunburstProps {
  files: ProviderFile[];

  selected?: string;
  onChoose: (element: string) => void;
}

const Sunburst: React.FC<SunburstProps & SizeProps> = ({ files, size, selected, onChoose }) => {
  const [hovered, setHovered] = useState<
    { file: HierarchyRectangularNode<ProviderFile>; x: number; y: number } | undefined
  >();

  const theme = useTheme();
  const radius = Math.min(size.height, size.width) / 2;

  const arc = useMemo(() => {
    const arc = d3
      .arc<unknown, HierarchyRectangularNode<ProviderFile>>()
      .startAngle((d) => d.x0)
      .endAngle((d) => d.x1)
      .innerRadius((d) => d.y0)
      .outerRadius((d) => d.y1);
    return arc;
  }, []);

  const mkFilePiece = (f: ProviderFile): ProviderFile => ({ ...f });

  const partition = useMemo(() => {
    const d = files.filter((file) => file.parserData !== undefined);

    const dirs = d
      .reduce(
        (arr, f) => {
          const p = f.path.split("/");
          return arr.concat(
            p.reduce<string[]>((path, el: string) => {
              const newEl = `${path[path.length - 1] ?? "sunburst"}/${el}`;
              if (arr.includes(path[path.length - 1])) return Object.assign([], path, { [path.length - 1]: newEl });
              return path.concat(newEl);
            }, []),
          );
        },
        ["sunburst"],
      )
      .map((path, id) =>
        mkFilePiece({
          id,
          exportId: 0,
          providerId: "",
          compressedSize: 0,
          fileName: path.substring(path.lastIndexOf("/") + 1),
          zipName: "",
          fileType: FileCategory.Other,
          dataCategory: Category.Other,
          folder: "",
          hash: "",
          isDir: true,
          path,
          data: [],
          uncompressedSize: 0,
          groupBy: "",
          date: new Date(),
          binaryData: null,
          mimeType: "unknown/unknown",
          parserData: {
            structure: undefined,
            byteData: undefined,
            algoStep: AlgoStep.ExtractData,
          },
        }),
      );

    const root = [
      ...dirs,
      ...d.map((f) => ({
        ...f,
        path: `sunburst/${f.path}/${f.fileName}`,
      })),
    ];

    const d3root = d3
      .stratify<ProviderFile>()
      .parentId((d) => d.path.substring(0, d.path.lastIndexOf("/")))
      .id((file) => file.path)(root)
      .sum((d) => d.data.length)
      .sort((a, b) => d3.descending(a.value, b.value));
    const partition = d3.partition<ProviderFile>().size([2 * Math.PI, radius])(d3root);
    return partition;
  }, [files, radius]);

  const color = (f: HierarchyRectangularNode<ProviderFile>) => {
    const traverseTree = (f: HierarchyRectangularNode<ProviderFile>): string | undefined | null => {
      return f.children?.reduce(
        (found, c) => {
          let val;
          if (c.data.isDir) {
            val = traverseTree(c);
          } else {
            val = theme.palette.categories[c.data.data[0]?.category ?? Category.Other].main;
          }

          if (found === undefined || found === val) return val;
          return null;
        },
        undefined as string | undefined | null,
      );
    };

    let isSelected = false;
    try {
      if (selected && f.data.path.startsWith(`sunburst/${getProvider(f.data.providerId).name}${selected}`))
        isSelected = true;
    } catch {
      // Not supposed to crash, simply do not select
    }

    if (isSelected) return "#f00";

    if (!f.data.isDir) return theme.palette.categories[f.data.data[0]?.category ?? Category.Other].main;

    return traverseTree(f) ?? "#888";
  };

  const getCategory = (node: HierarchyRectangularNode<ProviderFile>): Category => {
    if (!node.data.isDir) {
      return node.data.data[0]?.category ?? Category.Other;
    }

    const cat = node.children?.reduce<Category | undefined | null>((cat, child) => {
      if (cat === null) return null;
      const childCat = getCategory(child);
      if (cat === undefined) return childCat;
      return childCat === cat ? cat : null;
    }, undefined);

    return cat ?? Category.Other;
  };

  return (
    <div style={{ height: size.height }}>
      <svg viewBox={`0, 0, ${size.width}, ${size.height}`}>
        <g transform={`translate(${size.width / 2}, ${size.height / 2})`}>
          <g>
            {partition
              .descendants()
              .slice(1)
              .map((node) => (
                <path
                  key={node.id}
                  onMouseOver={({ clientX, clientY }) => setHovered({ file: node, x: clientX, y: clientY })}
                  onMouseMove={({ clientX, clientY }) => setHovered({ file: node, x: clientX, y: clientY })}
                  onMouseOut={() => setHovered(undefined)}
                  onClick={() => onChoose(node.data.path.substring(node.data.path.indexOf("/", 9)))} // Remove suffix sunburst/{PROVIDERNAME}, 'sunburst/'.length === 9
                  fill={color(node)}
                  stroke="#000"
                  strokeWidth={0.5}
                  d={arc(node) ?? undefined}
                />
              ))}
          </g>
        </g>
      </svg>
      <Tooltip
        settings={{
          tooltipWidth: 300,
          margin: 2,
        }}
        visible={hovered !== undefined}
        position={{
          x: hovered?.x ?? 0,
          y: hovered?.y ?? 0,
        }}
        texts={{
          title: hovered?.file.data.fileName,
          subtitle: "",
          body: "",
        }}
        category={hovered ? getCategory(hovered?.file) : Category.Other}
        iconType={"file"}
      />
    </div>
  );
};

export default ResizeComponent(Sunburst);
